/**
 * TeamsCard
 *
 * Displays the list of teams/roles with their permissions status.
 * Provides quick access to edit teams and configure permissions.
 */

import { useState } from 'react';
import useOrganizationGroups from 'src/api/organization/useOrganizationGroups';
import GroupDTO from 'src/types/user-management/GroupDTO';
import { Button } from '@/components/ui/button';
import { Spinner } from '@/components/ui/spinner';
import { Badge } from '@/components/ui/badge';
import {
  PencilIcon,
  TrashIcon,
  PlusIcon,
  UsersIcon,
  ShieldIcon,
  CaretRightIcon,
  GearSixIcon,
} from '@phosphor-icons/react';
import { cn } from '@/lib/utils';

import { TeamOnboardingModal } from './TeamOnboardingModal';
import { EditTeamModal } from './EditTeamModal';

export function TeamsCard() {
  const {
    data,
    isLoading,
    error,
    refetch: refetchGroups,
  } = useOrganizationGroups();

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedGroupToEdit, setSelectedGroupToEdit] = useState<GroupDTO>();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Spinner className="h-6 w-6" />
        <span className="ml-2 text-muted-foreground">Loading teams...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-destructive py-4 px-3 text-center">
        <p>Error loading teams. Please try again.</p>
        <Button
          variant="outline"
          size="sm"
          onClick={() => refetchGroups()}
          className="mt-2"
        >
          Retry
        </Button>
      </div>
    );
  }

  const groups = data?.groups || [];

  return (
    <>
      <div className="py-2">
        {groups.length === 0 ? (
          <EmptyState onCreateClick={() => setShowCreateModal(true)} />
        ) : (
          <div className="space-y-3">
            {/* Header with create button */}
            <div className="flex items-center justify-between px-1">
              <p className="text-sm text-muted-foreground">
                {groups.length} team{groups.length !== 1 ? 's' : ''} configured
              </p>
              <Button
                size="sm"
                onClick={() => setShowCreateModal(true)}
                className="h-8"
              >
                <PlusIcon className="h-4 w-4 mr-1.5" />
                Create Team
              </Button>
            </div>

            {/* Team list */}
            <div className="space-y-2">
              {groups.map((group) => (
                <TeamRow
                  key={group.id}
                  group={group}
                  onEdit={() => setSelectedGroupToEdit(group)}
                />
              ))}
            </div>
          </div>
        )}
      </div>

      {showCreateModal && (
        <TeamOnboardingModal
          open={true}
          onClose={(refetch?: boolean) => {
            if (refetch) {
              refetchGroups();
            }
            setShowCreateModal(false);
          }}
        />
      )}

      {selectedGroupToEdit && (
        <EditTeamModal
          team={selectedGroupToEdit}
          onClose={(refetch?: boolean) => {
            if (refetch) {
              refetchGroups();
            }
            setSelectedGroupToEdit(undefined);
          }}
        />
      )}
    </>
  );
}

interface TeamRowProps {
  group: GroupDTO;
  onEdit: () => void;
}

function TeamRow({ group, onEdit }: TeamRowProps) {
  return (
    <div
      className={cn(
        'group relative rounded-lg border bg-card p-3 transition-all cursor-pointer',
        'hover:border-muted-foreground/30 hover:shadow-sm hover:bg-accent/30',
      )}
      onClick={onEdit}
    >
      <div className="flex items-center gap-3">
        {/* Icon */}
        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
          <UsersIcon className="h-4 w-4" />
        </div>

        {/* Info */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2">
            <span className="font-medium text-sm">{group.name}</span>
            <Badge variant="secondary" className="text-xs h-5">
              {group.userCount} member{group.userCount !== 1 ? 's' : ''}
            </Badge>
          </div>
          {group.description ? (
            <p className="text-xs text-muted-foreground truncate mt-0.5">
              {group.description}
            </p>
          ) : (
            <p className="text-xs text-muted-foreground/60 italic mt-0.5">
              No description
            </p>
          )}
        </div>

        {/* Actions */}
        <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
          <Button
            variant="ghost"
            size="sm"
            className="h-8 px-2 text-xs"
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
          >
            <ShieldIcon className="h-3.5 w-3.5 mr-1" />
            Permissions
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            onClick={(e) => {
              e.stopPropagation();
              onEdit();
            }}
          >
            <GearSixIcon className="h-4 w-4" />
          </Button>
        </div>

        {/* Chevron indicator */}
        <CaretRightIcon className="h-4 w-4 text-muted-foreground/40 group-hover:text-muted-foreground transition-colors shrink-0" />
      </div>
    </div>
  );
}

interface EmptyStateProps {
  onCreateClick: () => void;
}

function EmptyState({ onCreateClick }: EmptyStateProps) {
  return (
    <div className="flex flex-col items-center justify-center text-center py-12 px-4">
      <div className="flex h-14 w-14 items-center justify-center rounded-full bg-gradient-to-br from-indigo-500/20 to-purple-500/20 mb-4">
        <UsersIcon className="h-7 w-7 text-indigo-600 dark:text-indigo-400" />
      </div>
      <h3 className="text-lg font-semibold mb-1">No teams yet</h3>
      <p className="text-muted-foreground text-sm max-w-sm mb-6">
        Teams define roles and permissions for your organization. Create teams
        to control what each role can access and modify.
      </p>
      <Button onClick={onCreateClick} size="lg">
        <PlusIcon className="h-4 w-4 mr-2" />
        Create Your First Team
      </Button>
    </div>
  );
}

export default TeamsCard;
